import { BaseRes, PageQuery } from 'types/base';
import {
  OmsOrder,
  OmsOrderDetail,
  OmsOrderMultiplePay,
  OmsRecyclingList,
  OmsRecycleOrderPayload,
  OmsRecycleOrderValuationPayload,
  OmsRecycleOrderDetail,
  OmsRecycleOrderFirstValuationSaveInfo,
  OmsRecycleOrderStatusTotal,
  OmsAppointmentPayload,
  OmsAppointmentDateVO,
  OmsAppointmentList,
  OmsAppointmentDateListVO,
  OmsOperateAppointment,
  OmsAppointmentDateUpdate,
  OmsAppointmentStatusTotal,
  OmsAppointmentStoreRecord,
  OmsAppointmentRecordInfoVO,
  OmsRecycleOrderCreateDTO,
  OmsAppointmentRecordDTO,
  omsRecycleOrderCreateLogisticsAddressDTO,
  OmsRecycleOrderFillInMemberPaymentInfoDTO,
  OmsRecycleOrder,
  OrderGenerateForMemberDto,
  StoreConfirmOrder,
  omsStoreFinalPaymentPayload,
  OmsOneCompletionPayload,
  StoreCreateOrderDto,
  StoreConfirmOrderDto,
  OmsConsignmentToRecycling,
  RecycleOrderReception,
  RecycleOrderReceptionList,
  RecycleOrderReceptionListQuery,
  RecycleOrderReceptionPayload,
  RecycleOrderReceptionPrefill,
  RecycleOrderReceptionQuery,
  OmsOrderItem,
  OmsOrderPayment,
  OrderTaxRegime,
} from 'types/oms';
import request, { ginzaxiaomaApiRequest } from '.';
import { PmsProduct } from 'types/pms';

export type GetOrderListDto = {
  memberId?: string;
  status?: number | string | null;
  statuses?: number[];
  start?: string;
  end?: string;
  createdFroms?: number[];
  receiverKeyword?: string;
  deliveryType?: string;
  tag?: string[];
  currencyList?: string[];
  staffName?: string;
  orderSn?: string;
  productSn?: string;
  /** 订单备注，模糊匹配 */
  note?: string;
  /** 为 true 时只返回实际币种运费大于 0 的订单 */
  freightGtZero?: boolean;
} & PageQuery;

export const getOrderList = (data: GetOrderListDto) => {
  return request.post<
    BaseRes<{
      list: OmsOrder[];
      total: number;
    }>
  >('/oms/order/list', data, {
    params: {
      pageNum: data.pageNum,
      pageSize: data.pageSize,
    },
  });
};

export const getMemberOrderListV2 = (data: {
  memberId: number;
  pageNum: number;
  pageSize: number;
}) => {
  return ginzaxiaomaApiRequest.post<{
    list: (Pick<
      OmsOrder,
      | 'id'
      | 'status'
      | 'createTime'
      | 'payAmountActualCurrency'
      | 'payAmount'
      | 'createdFrom'
      | 'staffName'
    > & {
      currency: string;
      orderItems: (Pick<OmsOrderItem, 'id' | 'productId' | 'actualCurrency'> & {
        product: Pick<
          PmsProduct,
          'id' | 'name' | 'note' | 'color' | 'stockStatus'
        > & {
          detailTitle: string;
        };
      })[];
    })[];
    total: number;
  }>('/admin/umsMember/order-list', data);
};

export const getMemberOrderListSmall = (data: {
  memberId: number;
  pageNum: number;
  pageSize: number;
}) => {
  return ginzaxiaomaApiRequest.post<{
    list: Pick<
      OmsOrder,
      | 'id'
      | 'status'
      | 'createTime'
      | 'payAmountActualCurrency'
      | 'payAmount'
      | 'createdFrom'
    >[];
    total: number;
  }>('/admin/umsMember/order-list-small', data);
};

export type GetOrderListV2Res = {
  list: (Pick<
    OmsOrder,
    | 'id'
    | 'orderSn'
    | 'deliveryType'
    | 'memberUsername'
    | 'totalAmountActualCurrency'
    | 'memberId'
    | 'orderType'
    | 'payAmount'
    | 'payAmountActualCurrency'
    | 'createdFrom'
    | 'createTime'
    | 'multiplePayStatus'
    | 'status'
    | 'totalTaxAmount'
    | 'paymentTime'
    | 'deliveryTime'
    | 'staffName'
    | 'note'
  > & {
    orderItems: (Pick<
      OmsOrderItem,
      | 'id'
      | 'orderId'
      | 'productSn'
      | 'productPic'
      | 'productId'
      | 'actualCurrency'
    > & {
      product: Pick<
        PmsProduct,
        'productSn' | 'description' | 'costPrice' | 'price' | 'stockPlace'
      >;
    })[];
    tagList: {
      id: number;
      orderId: number;
      tag: string;
    }[];
    orderPayList: Pick<
      OmsOrderPayment,
      'payType' | 'payStatus' | 'payAmount'
    >[];
  })[];
  total: number;
};

export const getOrderListV2 = (data: GetOrderListDto) => {
  return ginzaxiaomaApiRequest.post<GetOrderListV2Res>(
    '/admin/order/search/list',
    data,
  );
};

export const getOmsOrderDetailById = (id: OmsOrder['id']) => {
  return request.get<BaseRes<OmsOrderDetail>>(`/oms/order/${id}`);
};

export const getOmsOrderDetailV2 = (id: number) => {
  return ginzaxiaomaApiRequest.get<
    Pick<OmsOrderDetail, 'id' | 'b2bPrice' | 'invoiceNo'> & {
      /** Nest 返回；Java 详情无此字段 */
      taxRegime?: OrderTaxRegime | null;
      logisticsCost?: number;
      /** 免运费原因：DOMESTIC 境内配送，MEMBER GOLD/PLATINUM 会员 */
      freightWaiverReason?: 'DOMESTIC' | 'MEMBER' | null;
      tagList: {
        id: number;
        tag: string;
      }[];
      coupon?: {
        name?: string;
        note?: string;
        i18nDescription?: {
          [key: string]: {
            title: string;
            description: string;
          };
        };
      };
      orderPayList: {
        id: string;
        createTime: string;
        receiptKeys?: string[] | null;
      }[];
    }
  >(`/admin/order/detail/${id}`);
};

export const updateOmsOrderDetail = (
  id: number,
  data: Pick<OmsOrder, 'b2bPrice' | 'staffName' | 'memberId' | 'note'> & {
    tagList?: string[];
    isPointsGiven?: boolean;
    createdFrom?: number;
  },
) => {
  return ginzaxiaomaApiRequest.put(`/admin/order/update/${id}`, data);
};

/**
 * 调整订单优惠金额（仅 admin 可用）
 * - 待付款：重算应付；多笔支付同步最后一笔未付 needPayAmount
 * - 待发货/已发货：仅支持增加优惠，并同步扣减 SUCCESS 支付记录
 */
export const adjustOrderPromotion = (
  id: number,
  data: { promotionAmountActualCurrency: number },
) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/adjust-promotion/${id}`,
    data,
  );
};

/**
 * 调整订单运费（仅 isAdmin 可用）
 * 待付款：写入运费新值，同步更新订单总金额与应付金额
 */
export const updateOrderLogisticsCost = (
  id: number,
  data: { logisticsCost: number },
) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/update-logistics-cost/${id}`,
    data,
  );
};

export const adjustOrderFreight = (
  id: number,
  data: { freightAmountActualCurrency: number },
) => {
  return ginzaxiaomaApiRequest.post(`/admin/order/adjust-freight/${id}`, data);
};

export type ConvertOrderToTaxInclusivePreview = {
  orderId: number;
  currency: string;
  oldPayAmountActualCurrency: number;
  newPayAmountActualCurrency: number;
  oldTotalAmountActualCurrency: number;
  newTotalAmountActualCurrency: number;
  oldTotalTaxAmount: number;
  newTotalTaxAmount: number;
  paidAmount: number;
  taxDiff: number;
  items: Array<{
    productId: number;
    productName: string;
    productQuantity: number;
    oldProductPrice: number;
    newProductPrice: number;
    oldTaxAmount: number;
    newTaxAmount: number;
  }>;
};

/** 预览：待发货、已发货或已完成的免税订单改为含税价后的金额与补差价 */
export const previewConvertOrderToTaxInclusive = (id: number) => {
  return ginzaxiaomaApiRequest.get<ConvertOrderToTaxInclusivePreview>(
    `/admin/order/convert-to-tax-inclusive/${id}/preview`,
  );
};

/** 待发货、已发货或已完成的免税订单改为含税价，生成补差价待支付分期并改回待付款 */
export const convertOrderToTaxInclusive = (id: number) => {
  return ginzaxiaomaApiRequest.post<ConvertOrderToTaxInclusivePreview>(
    `/admin/order/convert-to-tax-inclusive/${id}`,
  );
};

/** 待支付订单追加使用积分（仅 admin 可用），会扣除会员积分并重算应付金额 */
export const adjustOrderUseIntegration = (
  id: number,
  data: { additionalUseIntegration: number },
) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/adjust-use-integration/${id}`,
    data,
  );
};

export const getOmsOrderMultiplePayList = (id: OmsOrder['id']) => {
  return ginzaxiaomaApiRequest.get<OmsOrderMultiplePay[]>(
    `/admin/order/multiple-pay-set-info/${id}`,
  );
};

export const setOrderPayReceiptImages = (params: {
  orderId: number;
  payId: string;
  keys: string[];
}) => {
  return ginzaxiaomaApiRequest.put(
    `/admin/order/${params.orderId}/payments/${params.payId}/receipt-images`,
    {
      keys: params.keys,
    },
  );
};

export const uploadOrderFile = (orderId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return ginzaxiaomaApiRequest.post<{
    url: string;
    key: string;
    mimetype: string;
    originalname: string;
    size: number;
  }>(`/admin/order/${orderId}/upload-file`, formData);
};

export const updateOrderPayType = (params: {
  orderId: number;
  payId: string;
  payType: string;
}) => {
  return ginzaxiaomaApiRequest.put(
    `/admin/order/${params.orderId}/payments/${params.payId}/pay-type`,
    {
      payType: params.payType,
    },
  );
};

export const updateOrderPayAmount = (params: {
  orderId: number;
  payId: string;
  payAmount: number;
}) => {
  return ginzaxiaomaApiRequest.put(
    `/admin/order/${params.orderId}/payments/${params.payId}/pay-amount`,
    {
      payAmount: params.payAmount,
    },
  );
};

export const setOmsOrderMultiplePayList = (
  id: OmsOrder['id'],
  data: OmsOrderMultiplePay[],
) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/multiple-pay-set-edit/${id}`,
    { list: data },
  );
};

type OmsOrderReceiverInfoUpdateDto = {
  orderId: number;
  status: number;
  receiverName: string;
  receiverPhone: string;
  receiverPostCode: string;
  receiverDetailAddress: string;
  receiverProvince: string;
  receiverCity: string;
};

export const omsOrderReceiverInfoUpdate = (
  data: OmsOrderReceiverInfoUpdateDto,
) => {
  return request.post('/oms/order/update/receiverInfo', data);
};

type OmsOrderAddOperationNoteDto = {
  id: number;
  note: string;
  status: number;
  operationImages?: string;
  operationImagesUrl?: string;
  isSendNotice?: boolean;
};

export const omsOrderAddOperationNote = (data: OmsOrderAddOperationNoteDto) => {
  return request.post('/oms/order/update/note', {}, { params: data });
};

export const omsOrderAddOperationNoteV2 = (
  data: OmsOrderAddOperationNoteDto,
) => {
  return ginzaxiaomaApiRequest.post('/admin/order/update-note', data);
};

export type SalesManagementDto = {
  createdFrom?: number;
  pageNum?: number;
  pageSize?: number;
};

export type SalesManagementItem = {
  deliveryTime: string;
  orderId: string;
  productId: string;
  productDescription: string;
  detailTitle: string | null;
  rank: string;
  costPrice: string;
  price: string;
  costPriceWithoutTax: string;
  priceWithoutTax: string;
  grossProfitWithoutTax: string;
  integration: string | null;
  integrationAmount: string;
  productQuantity: number;
  sourceType: number;
  recycleStaffName: string;
  staffName: string;
  note: string | null;
  promotionAmountActualCurrency: string | null;
  entryTime: string;
  receptionistId: number | null;
};

export const getSalesManagement = (params: SalesManagementDto) => {
  return ginzaxiaomaApiRequest.get<{
    list: SalesManagementItem[];
    total: number;
  }>('/admin/order/sales-management', { params });
};

type OmsOrderDeliverUpdateDto = {
  orderId: number;
  deliveryCompany: string;
  deliverySn: string;
  isSendEmail: boolean;
}[];

export const omsOrderDeliverUpdate = (data: OmsOrderDeliverUpdateDto) => {
  return ginzaxiaomaApiRequest.post('/admin/order/update/delivery', data);
};

export const omsOrderFinish = ({
  orderId,
  ...rest
}: {
  orderId: number;
  note?: string;
  isPointsGiven: boolean;
}) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/order-finish/${orderId}`,
    rest,
  );
};

export const omsOrderRevertToPendingDelivery = (orderId: number) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/revert-to-pending-delivery/${orderId}`,
  );
};

export const omsOrderRevertToDelivered = (orderId: number) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/revert-to-delivered/${orderId}`,
  );
};

export const cancelOrderByAdmin = (data: {
  id: number;
  note: string;
  refundMethod: string;
  isSendEmail: boolean;
}) => {
  return ginzaxiaomaApiRequest.post('/admin/order/cancel-order', data);
};

/** 待发货订单直接取消，仅 admin */
export const cancelPendingDeliveryOrder = (orderId: number) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/cancel-pending-delivery/${orderId}`,
  );
};

export const omsOrderOfflinePayAccept = (data: {
  orderId: number;
  note: string;
}) => {
  return ginzaxiaomaApiRequest.post('/admin/order/bank-transfer-review', {
    ...data,
    isPass: true,
  });
};

export const omsOrderOfflinePayReject = (data: {
  orderId: number;
  note: string;
}) => {
  return ginzaxiaomaApiRequest.post('/admin/order/bank-transfer-review', {
    ...data,
    isPass: false,
  });
};

/** 回收寄卖列表 */
export const fetchOmsRecyclingList = (data: OmsRecycleOrderPayload) => {
  return request
    .post<BaseRes<OmsRecyclingList>>('/oms/recycleOrder/page', data, {
      params: {
        pageNum: data.pageNum,
        pageSize: data.pageSize,
      },
    })
    .then((res) => {
      res.data.list.forEach((i) => {
        // @ts-ignore 这里是因为列表页返回的日志信息是 omsRecycleOrderLogs 字段
        // 为了和详情页 omsRecycleOrderLogList 统一，这里强制改了下
        i.omsRecycleOrderLogList = i.omsRecycleOrderLogs;
      });
      return res;
    });
};

/** 初步估值 */
export const fetchFirstValuation = (data: OmsRecycleOrderValuationPayload) => {
  return request.post<BaseRes<string>>(
    '/oms/recycleOrder/firstValuation',
    data,
  );
};

/** 拒绝估值 */
export const fetchTerminationFirstValuation = (data: {
  remark?: string;
  id?: string /** 回收寄卖ID */;
}) => {
  return request.post<BaseRes<string>>(
    '/oms/recycleOrder/terminationFirstValuation',
    data,
  );
};

/** 上传shiplable */
export const fetchUploadShiplable = (data: {
  id?: string /** 回收寄卖ID */;
  shippingLabel?: string /** 物流单 */;
}) => {
  return request.post<BaseRes<string>>(
    '/oms/recycleOrder/createLogisticsCertificate',
    data,
  );
};

/** 确认收货 */
export const fetchGoodsReceived = (data: {
  content?: string /** 收货内容 */;
  id?: string /** 回收寄卖ID */;
  receiptPics?: string /** 收货图片 */;
}) => {
  return request.post<BaseRes<string>>('/oms/recycleOrder/goodsReceived', data);
};

/** 最终报价 */
export const fetchFinalValuation = (data: {
  finalRecyclePrice?: number /** 最终回收价格 */;
  finalSalePrice?: number /** 最终售卖价格 */;
  omsRecycleOrderItemId?: string /** 回收寄卖商品ID */;
  omsRecycleOrderType?: number; // 回收类型 : 1-寄卖，2-回收
  settlementType?: number; // 订单结算类型：1-现金结算 2-财务打款
  emailRemark?: string; // 邮件备注
  currency?: string;
  reviewerName?: string; // 复核确认人
}) => {
  return request.post<BaseRes<string>>(
    '/oms/recycleOrder/finalValuation',
    data,
  );
};

/** 拒绝最终报价 */
export const fetchterminationFinalValuation = (data: {
  remark?: string;
  id?: string /** 回收寄卖ID */;
  reviewerName?: string; // 复核确认人
}) => {
  return request.post<BaseRes<string>>(
    '/oms/recycleOrder/terminationFinalValuation',
    data,
  );
};

/** 回收寄卖详情 */
export const fetchRecycleOrderDetail = (data: {
  id?: string /** 回收寄卖ID */;
}) => {
  return request.post<BaseRes<OmsRecycleOrderDetail>>(
    `/oms/recycleOrder/info/${data.id}`,
  );
};

/** 上传物流凭证 */
export const fetchUploadShippingDocument = (data: {
  id?: string /** 回收寄卖ID */;
  stateWrapperShippingDocument?: string;
}) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/uploadStateWrapperShippingDocument`,
    data,
  );
};

/** 修改最终报价 */
export const fetchUpdateFinalValuation = (data: {
  finalRecyclePrice?: number /** 最终回收价格 */;
  finalSalePrice?: number /** 最终售卖价格 */;
  id?: string /** 回收寄卖ID */;
  emailRemark?: string; // 邮件备注
}) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/updateFinalValuation`,
    data,
  );
};

/** 确认结算 */
export const fetchConfirmSettlement = (data: {
  settlementType?: number /** 订单结算类型：1-现金结算 2-财务打款 */;
  id?: string /** 回收寄卖ID */;
}) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/confirmSettlement`,
    data,
  );
};

/** 确认退货 */
export const fetchConfirmReturn = (data: {
  id?: string /** 回收寄卖ID */;
  returnType?: number /** 退货类型：1:门店自提，2:邮寄 */;
  returnVoucher?: string /** 退货凭证 */;
  returnExpressCompany?: string /** 退货快递公司 */;
  returnTrackingNumber?: string /** 退货快递单号  */;
}) => {
  return request.post<BaseRes<string>>(`/oms/recycleOrder/confirmReturn`, data);
};

/** 录入打款信息 / 重复打款信息 */
export const fetchPaymentVoucher = (formData: FormData) => {
  return ginzaxiaomaApiRequest.post<BaseRes<void>>(
    '/admin/oms-recycle-order/payment-voucher-entry',
    formData,
  );
};

/** 发送签署合同 PDF 邮件给客户 */
export const sendSignedContractEmail = (formData: FormData) => {
  return ginzaxiaomaApiRequest.post<
    BaseRes<{
      message: string;
      signedContractFileKey: string;
      recipientEmail: string;
      orderIds: string[];
    }>
  >('/admin/oms-recycle-order/send-signed-contract', formData);
};

/** 查询已发送的签署合同信息 */
export const fetchSignedContractInfo = (id: string) => {
  return ginzaxiaomaApiRequest.get<{
    signedContractFileKey?: string | null;
    signedContractSentTime?: string | null;
    downloadUrl?: string | null;
  }>(`/admin/oms-recycle-order/signed-contract-info/${id}`);
};

/** 下载已发送的签署合同 PDF */
export const downloadSignedContract = (id: string) => {
  return ginzaxiaomaApiRequest.get<Blob>(
    `/admin/oms-recycle-order/signed-contract-download/${id}`,
    { responseType: 'blob' },
  );
};

export const batchFetchPaymentVoucher = (formData: FormData) => {
  return ginzaxiaomaApiRequest.post<BaseRes<void>>(
    '/admin/oms-recycle-order/payment-voucher-entry/batch',
    formData,
  );
};

/** 初步估值前补充信息 */
export const fetchFirstValuationSaveInfo = (
  data: OmsRecycleOrderFirstValuationSaveInfo,
) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/firstValuationSaveInfo`,
    data,
  );
};

/** 订单取消 */
export const fetchCancelRecycleOrder = (data: {
  /** 回收寄卖ID */
  id?: string;
  /** 原因 */
  remark?: string;
}) => {
  return request.post<BaseRes<string>>(`/oms/recycleOrder/cancel`, data);
};

/** 状态总数 */
export const fetchStatusTotal = () => {
  return request.post<BaseRes<OmsRecycleOrderStatusTotal>>(
    `/oms/recycleOrder/getPageStatusTotal`,
  );
};

/** 预约视图 */
export const fetchWeekTotalList = (data: OmsAppointmentPayload) => {
  return request.post<BaseRes<OmsAppointmentDateVO[]>>(
    `/oms/appointmentStoreRecord/weekTotalList`,
    data,
  );
};

/** 预约列表 */
export const fetchAppointmentStoreRecordPage = (
  data: OmsAppointmentPayload,
) => {
  return request.post<BaseRes<OmsAppointmentList>>(
    `/oms/appointmentStoreRecord/page`,
    data,
  );
};

/** 预约配置表 */
export const fetchAppointmentDateList = (storeId: number) => {
  return request.post<BaseRes<OmsAppointmentDateListVO[]>>(
    `/oms/appointmentStoreRecord/appointmentDateList?storeId=${storeId}`,
  );
};

/** 列表状态 */
export const fetchOperateAppointmentDate = (data: OmsOperateAppointment) => {
  return request.post<BaseRes<string>>(
    `/oms/appointmentStoreRecord/operateAppointmentDate`,
    data,
  );
};

/** 某天时间段调整 */
export const fetchUpdateAppointmentDate = (data: OmsAppointmentDateUpdate) => {
  return request.post<BaseRes<string>>(
    `/oms/appointmentStoreRecord/modifyAppointmentTime`,
    data,
  );
};

/** 预约总览状态数 */
export const fetchAppointmentStatusTotal = (storeId: number) => {
  return request.post<BaseRes<OmsAppointmentStatusTotal>>(
    `/oms/appointmentStoreRecord/getPageStatusTotal?storeId=${storeId}`,
  );
};

/** 创建预约 */
export const fetchAppointmentCreate = (data: OmsAppointmentStoreRecord) => {
  return request.post<BaseRes<string>>(
    `/oms/appointmentStoreRecord/create`,
    data,
  );
};

/** 修改预约 */
export const fetchAppointmentUpdate = (data: OmsAppointmentStoreRecord) => {
  return request.post<BaseRes<string>>(
    `/oms/appointmentStoreRecord/update`,
    data,
  );
};

/** 查询用户已初步估值订单信息 */
export const fetchRecycleOrderInfo = (memberId: number) => {
  return request.post<BaseRes<OmsRecycleOrderDetail[]>>(
    `/oms/appointmentStoreRecord/omsRecycleOrderInfo?memberId=${memberId}`,
  );
};

/** 取消预约 */
export const fetchCancelRecycle = (appointmentId: string) => {
  return request.post<BaseRes<string>>(
    `/oms/appointmentStoreRecord/cancel?appointmentId=${appointmentId}`,
  );
};

/** 预约详情 */
export const fetchAppointmentStoreRecordInfo = (id: string) => {
  return request.post<BaseRes<OmsAppointmentRecordInfoVO>>(
    `/oms/appointmentStoreRecord/info/${id}`,
  );
};

// 修改订单信息
export const modifyOmsRecycleOrderDetails = (data: {
  id: string;
  logisticsName?: string;
  logisticsPhone?: string;
  logisticsPhone2?: string;
  logisticsPostCode?: string;
  logisticsCountry?: string;
  logisticsCity?: string;
  logisticsDetailAddress?: string;
  memberCredentialNo?: string;
  memberPaymentBankCountry?: string;
  memberPaymentBankNo?: string;
  memberPaymentBankName?: string;
  memberPaymentBankAddress?: string;
  memberPaymentSwiftCode?: string;
  memberPaymentAccountType?: string;
  memberPaymentAccountSubType?: string;
  memberPaymentRoutingNo?: string;
  memberPaymentFpsNo?: string;
  memberPaymentFpsAccountName?: string;
  memberPaymentBankPhone?: string;
  memberPaymentPayNowNo?: string;
  memberPaymentPayNowAccountName?: string;
  memberPaymentAccountNo?: string;
  memberPaymentAccountName?: string;
  memberPaymentAccountAddress?: string;
  memberPaymentKiGo?: string;
  memberPaymentBangGo?: string;
}) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/modifyOmsRecycleOrderDetails`,
    data,
  );
};

/** 查询用户当前是否有未完成的预约 */
export const fetchUnFinishedAppointmentList = ({
  memberId,
  email,
}: {
  memberId?: number;
  email?: string;
}) => {
  return request.post<BaseRes<OmsAppointmentStoreRecord[]>>(
    `/oms/appointmentStoreRecord/unfinishedAppointmentList?memberId=${
      memberId || ''
    }&email=${email}`,
  );
};

/** 创建订单 */
export const fetchRecycleOrderCreate = (data: OmsRecycleOrderCreateDTO) => {
  return request.post<BaseRes<string>>(`/oms/recycleOrder/create`, data);
};

/** 店铺可预约时间段 */
export const fetchShopValidReservationTime = (storeId: number) => {
  return request.post<BaseRes<OmsAppointmentDateListVO[]>>(
    `/oms/appointmentStoreRecord/shopValidReservationTime?storeId=${storeId}`,
  );
};

/** sns订单-提交预约信息 */
export const fetchOrderReservation = (data: OmsAppointmentRecordDTO) => {
  return request.post<BaseRes<OmsAppointmentDateListVO[]>>(
    `/oms/appointmentStoreRecord/snsOrderReservation`,
    data,
  );
};

/** sns订单-提交邮寄信息 */
export const fetchCreateLogisticsAddress = (
  data: omsRecycleOrderCreateLogisticsAddressDTO,
) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/createLogisticsAddressAndMailingVoucher`,
    data,
  );
};

/** sns订单-确认最终报价 */
export const fetchAgreeFinalValuation = (data: {
  id: string;
  type: number;
}) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/agreeFinalValuation`,
    data,
  );
};

/** sns订单-打款 */
export const fetchFillInMemberPaymentInfo = (
  data: OmsRecycleOrderFillInMemberPaymentInfoDTO,
) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/fillInMemberPaymentInfo`,
    data,
  );
};

export const changeOrderPriority = (
  data: Pick<OmsRecycleOrder, 'id' | 'priority'>,
) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/changeOrderPriority`,
    data,
  );
};

export const omsOrderInvalid = (
  id: OmsOrder['id'],
  note: string,
  autoPublish: number,
) => {
  return ginzaxiaomaApiRequest.post(`/admin/order/orderInvalid/${id}`, {
    note,
    autoPublish,
  });
};

/**
 * 给已支付完成的订单发送支付成功邮件通知
 */
export const sendPaymentSuccessEmailByAdmin = (orderId: number) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/send-payment-success-email/${orderId}`,
  );
};

// 修改操作信息
export const modifyOmsOperation = (data: {
  id: string;
  shopRemark?: string;
}) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/addOmsRecycleOrderLog`,
    data,
  );
};

/** 订单计算合计金额 */
export const omsGenerateConfirmOrder = (
  data: Pick<
    OrderGenerateForMemberDto,
    | 'productIdList'
    | 'taxFreeProductIdList'
    | 'useIntegration'
    | 'promotionAmount'
    | 'memberId'
    | 'couponCode'
    | 'receiveAddressId'
    | 'orderStatus'
    | 'productQuantity'
    | 'manualFreight'
    | 'manualFreightAmountActualCurrency'
  >,
) => {
  return ginzaxiaomaApiRequest.post<StoreConfirmOrder>(
    `/admin/order/generate-confirm-order`,
    data,
  );
};

// 创建订单
export const omsGenerateForMember = (data: OrderGenerateForMemberDto) => {
  return ginzaxiaomaApiRequest.post<{ omsOrder: OmsOrder }>(
    `/admin/order/generate-for-member`,
    data,
  );
};

/** 创建收银台订单 */
export const omsCreateCheckoutCounter = (data: StoreCreateOrderDto) => {
  return ginzaxiaomaApiRequest.post<StoreConfirmOrder>(
    `/checkout-counter/store`,
    data,
  );
};

/** 合计金额 */
export const omsStoreConfirmOrder = (data: StoreConfirmOrderDto) => {
  return ginzaxiaomaApiRequest.post<StoreConfirmOrder>(
    `/checkout-counter/store-confirm-order`,
    data,
  );
};

/** 结算尾款 */
export const omsStoreFinalPayment = (data: omsStoreFinalPaymentPayload) => {
  return ginzaxiaomaApiRequest.post<StoreConfirmOrder>(
    `/checkout-counter/store-final-payment`,
    data,
  );
};

/** 一键完成 */
export const oneCompletion = (data: OmsOneCompletionPayload) => {
  return request.post(`/oms/recycleOrder/completeOrder`, data);
};

export const getOrderStatusStatistics = (
  data: Omit<GetOrderListDto, 'pageNum' | 'pageSize' | 'statuses'>,
) => {
  return ginzaxiaomaApiRequest.post<
    {
      status: number;
      count: string;
    }[]
  >('/admin/order/status-statistics', data);
};

// 修改订单信息
export const modifyOmsInitialValuation = (data: {
  id: string;
  currency: string;
  minRecyclePrice: number;
  maxRecyclePrice: number;
  minSalePrice: number;
  maxSalePrice: number;
}) => {
  return request.post<BaseRes<string>>(
    `/oms/recycleOrder/modifyInitialValuation`,
    data,
  );
};

// 手动完成多笔支付
export const omsOrderCompleteMultiplePay = (data: {
  orderId: number;
  multiplePaySetId: string;
  note?: string;
  payType?: string;
}) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/order/complete-multiple-pay-set`,
    data,
  );
};

export const consignmentToRecycling = (data: OmsConsignmentToRecycling) => {
  return ginzaxiaomaApiRequest.post<{ message: string }>(
    `/admin/oms-recycle-order/sale-to-recycle`,
    data,
  );
};

export const getTripleaPaymentDetail = (paymentReference: string) => {
  return ginzaxiaomaApiRequest.get(
    `/admin/order/triplea-payment-details/${paymentReference}`,
  );
};

export const productStoreTransfer = (data: {
  toShopId: number;
  fromShopId: number;
  productList: {
    id: number;
    b2bPrice: number;
  }[];
}) => {
  return ginzaxiaomaApiRequest.post<{
    omsOrder: { id: number };
    productIds: number[];
  }>(`/admin/order/product-store-transfer`, data);
};

export const omsOrderRecycleOrderUpdateInfo = (data: {
  omsRecycleOrderId: string;
  finalSalePrice?: number;
  finalRecyclePrice?: number;
  note?: string;
  operationImages?: string;
  operationImagesUrl?: string;
  isSendNotice?: boolean;
}) => {
  // 回收寄卖消息通知：默认不发送，需要发送时传 isSendNotice: true
  return ginzaxiaomaApiRequest.post(`/admin/oms-recycle-order/update-info`, {
    ...data,
    isSendNotice: data.isSendNotice ?? false,
  });
};

export const omsOrderRecycleOrderUpdateInfoV2 = (data: {
  omsRecycleOrderId: string;
  isTradeIn?: number;
  firstValuationShopRemark?: string;
  staffName?: string;
  receptionistId?: number;
}) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/oms-recycle-order/update-info-v2`,
    data,
  );
};

export const omsOrderRecycleOrderUpdateTimes = (data: {
  omsRecycleOrderId: string;
  createTime?: string;
  agreeFirstValuationTime?: string;
  agreeFinalValuationTime?: string;
  completeTime?: string;
  firstValuationTime?: string;
  finalValuationTime?: string;
  financialPaymentTime?: string;
}) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/oms-recycle-order/update-times`,
    data,
  );
};

export const getRelatedShipmentList = (orderId: number) => {
  return ginzaxiaomaApiRequest.get(`/admin/order/shipment-list/${orderId}`);
};

export const addOrUpdateRelatedShipment = (data: {
  orderId: number;
  shipments: any[];
}) => {
  return ginzaxiaomaApiRequest.post('/admin/order/shipment-addOrUpdate', data);
};

export const deleteRelatedShipmen = (id: number) => {
  return ginzaxiaomaApiRequest.delete(`/admin/order/shipment/${id}`);
};

export const getOmsRecycleOrderEmail = (id: string) => {
  return ginzaxiaomaApiRequest.get(
    `/admin/oms-recycle-order/related-email/${id}`,
  );
};

export const deleteRecycleOrder = (id: string) => {
  return ginzaxiaomaApiRequest.post(`/admin/oms-recycle-order/delete/${id}`);
};

export const convertRecycleOrderToConsignment = (id: string) => {
  return ginzaxiaomaApiRequest.post<{ message: string }>(
    `/admin/oms-recycle-order/convert-to-consignment/${id}`,
  );
};

/** 交易完成回收单取消（仅 admin）：1 退货完全取消 2 直接取消 */
export const cancelCompletedRecycleOrder = (
  id: string,
  data: { cancelMode: 1 | 2; remark?: string },
) => {
  return ginzaxiaomaApiRequest.post<{ message: string }>(
    `/admin/oms-recycle-order/cancel-completed/${id}`,
    data,
  );
};

/** 交易完成寄卖单因打款失败退回待打款（仅 admin） */
export const revertRecycleOrderToWaitPayment = (id: string) => {
  return ginzaxiaomaApiRequest.post<{ message: string }>(
    `/admin/oms-recycle-order/revert-to-wait-payment/${id}`,
  );
};

/** 待退货/已确认退货寄卖单恢复为寄卖中（仅 admin） */
export const restoreRecycleOrderConsignment = (
  id: string,
  data?: { republish?: boolean },
) => {
  return ginzaxiaomaApiRequest.post<{ message: string }>(
    `/admin/oms-recycle-order/restore-consignment/${id}`,
    data ?? {},
  );
};

export const sendOmsRecycleOrderFundsTransferNotification = (
  id: string,
  remark?: string,
) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/oms-recycle-order/send-funds-transfer-notification/${id}`,
    remark ? { remark } : undefined,
  );
};

export const sendOmsRecycleOrderFundsTransferNotificationBatch = (data: {
  omsRecycleOrderIdList: string[];
  remark?: string;
}) => {
  return ginzaxiaomaApiRequest.post<BaseRes<void>>(
    `/admin/oms-recycle-order/send-funds-transfer-notification-batch`,
    data,
  );
};

export const bindOmsRecycleOrderAuthenticationBag = (data: {
  omsRecycleOrderId: string;
  authenticationBagId: number;
}) => {
  return ginzaxiaomaApiRequest.post<{ message: string }>(
    `/admin/oms-recycle-order/bind-authentication-bag`,
    data,
  );
};

export const unbindOmsRecycleOrderAuthenticationBag = (data: {
  omsRecycleOrderId: string;
}) => {
  return ginzaxiaomaApiRequest.post<{ message: string }>(
    `/admin/oms-recycle-order/unbind-authentication-bag`,
    data,
  );
};

export interface MemberStatistics {
  currency: string;
  recycleAmount: number;
  consignmentAmount: number;
}

export const getMemberStatistics = (memberId: number) => {
  return ginzaxiaomaApiRequest.get<MemberStatistics[]>(
    `/admin/oms-recycle-order/member-statistics/${memberId}`,
  );
};

export const getProfitReport = (data: {
  staffList: string[];
  startCompleteTime?: string;
  endCompleteTime?: string;
  startDeliveryTime?: string;
  endDeliveryTime?: string;
  stockPlaceList?: string[];
  createdFromList?: number[];
}) => {
  return ginzaxiaomaApiRequest.post<{
    total: number;
    list: {
      id: number;
      orderSn: string;
      memberId?: number;
      memberUsername?: string;
      member?: {
        firstName?: string;
        lastName?: string;
      };
      staffName?: string;
      status: number;
      totalAmountActualCurrency: number;
      payAmountActualCurrency: number;
      currency: string;
      createTime: string;
      paymentTime?: string;
      deliveryTime?: string;
      receiveTime?: string;
      useIntegration?: number;
      integration?: number;
      promotionAmountActualCurrency?: number;
      freightAmountActualCurrency?: number;
      createdFrom?: number;
      couponAmount?: number;
      totalTaxAmount?: number;
      isB2B: boolean;
      // 关联支付记录（后端已筛选支付成功记录）
      orderPayList?: {
        payType?: string;
        payMode?: string;
        payStatus?: string;
        payAmount?: number;
      }[];
      orderItems: {
        actualCurrency: string;
        actualCurrencyRate: number;
        isTaxFree: number;
        productPriceActualCurrency: number;
        productQuantity: number;
        productId: number;
        product: {
          costPrice?: number;
          currency: string;
          id: number;
          stockPlace: string;
          name?: string;
          /** 商品来源类型：0-回收；1-寄卖；2-采购 */
          sourceType?: number;
        };
      }[];
    }[];
  }>(`/admin/order/profit-report`, data);
};

export const modifyOmsRecycleOrderPayoutDetail = (data: {
  omsRecycleOrderId: string;
  payoutDetail: string;
  settlementType: number;
}) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/oms-recycle-order/payout-detail`,
    data,
  );
};

export type CashPaymentSummaryItem = {
  submissionId: number;
  financialPaymentVoucher: string;
  orderCount: number;
  totalAmount: number;
};

export const getCashPaymentSummary = (shopId: number) => {
  return ginzaxiaomaApiRequest.get<CashPaymentSummaryItem[]>(
    `/admin/oms-recycle-order/cash-payment-summary/${shopId}`,
  );
};

export const getOrderDetailBatch = (data: { ids: number[] }) => {
  return ginzaxiaomaApiRequest.post<
    (Pick<
      OmsOrder,
      | 'id'
      | 'orderSn'
      | 'createTime'
      | 'paymentTime'
      | 'deliveryTime'
      | 'invoiceNo'
      | 'createdFrom'
      | 'freightAmountActualCurrency'
      | 'promotionAmountActualCurrency'
      | 'payAmountActualCurrency'
      | 'totalTaxAmount'
      | 'useIntegration'
      | 'receiverName'
      | 'receiverDetailAddress'
      | 'receiverCity'
      | 'receiverProvince'
      | 'receiverPhone'
    > & {
      taxRegime?: OrderTaxRegime | null;
      /** 跨店调货生成的新商品 ID，逗号分隔 */
      b2bProductId?: string | null;
      orderItems: (OmsOrderItem & {
        product: Pick<PmsProduct, 'id' | 'stockPlace'>;
      })[];
      orderPayList?: Pick<OmsOrderPayment, 'payType' | 'payAmount'>[];
      tagList?: { tag: string }[];
    })[]
  >(`/admin/order/detail/batch`, data);
};

export const ensureOrderInvoiceNos = (data: { ids: number[] }) => {
  return ginzaxiaomaApiRequest.post<{ id: number; invoiceNo: string }[]>(
    '/admin/order/invoice-no/ensure',
    data,
  );
};

export type OmsRecycleOrderDailyStat = {
  type: number; // 1 = 寄卖, 2 = 回收
  currency: string;
  count: number;
  totalAmount: number;
};

export const getRecycleOrderDailyStats = (shopId: number) => {
  return ginzaxiaomaApiRequest.get<OmsRecycleOrderDailyStat[]>(
    `/admin/oms-recycle-order/daily-stats/${shopId}`,
  );
};

/** 按一次交易读取買取・委託受付表。没有记录时 reception 为空，并带回预填 */
export const getRecycleOrderReception = (submissionId: number) => {
  return ginzaxiaomaApiRequest.get<RecycleOrderReceptionQuery>(
    '/admin/oms-recycle-order/reception',
    { params: { submissionId } },
  );
};

/** 按受付表 id 读取这一张 */
export const getRecycleOrderReceptionById = (id: number) => {
  return ginzaxiaomaApiRequest.get<RecycleOrderReception>(
    '/admin/oms-recycle-order/reception',
    { params: { id } },
  );
};

/** 选定会员，或从已有订单进入时的预填。不建表 */
export const getRecycleOrderReceptionPrefill = (params: {
  memberId?: number;
  submissionId?: number;
}) => {
  return ginzaxiaomaApiRequest.get<RecycleOrderReceptionPrefill>(
    '/admin/oms-recycle-order/reception/prefill',
    { params },
  );
};

export const listRecycleOrderReception = (
  params: RecycleOrderReceptionListQuery,
) => {
  return ginzaxiaomaApiRequest.get<RecycleOrderReceptionList>(
    '/admin/oms-recycle-order/reception/list',
    { params },
  );
};

/** 创建受付表。不带 submissionId 就是单独创建 */
export const createRecycleOrderReception = (
  data: RecycleOrderReceptionPayload,
) => {
  return ginzaxiaomaApiRequest.post<RecycleOrderReception>(
    '/admin/oms-recycle-order/reception',
    data,
  );
};

/** 改正这一张受付表，不改绑定的交易 */
export const updateRecycleOrderReception = (
  data: RecycleOrderReceptionPayload,
) => {
  return ginzaxiaomaApiRequest.put<RecycleOrderReception>(
    '/admin/oms-recycle-order/reception',
    data,
  );
};

export const bindRecycleOrderReception = (data: {
  id: number;
  submissionId: number;
}) => {
  return ginzaxiaomaApiRequest.post<RecycleOrderReception>(
    '/admin/oms-recycle-order/reception/bind',
    data,
  );
};

export const unbindRecycleOrderReception = (id: number) => {
  return ginzaxiaomaApiRequest.post<RecycleOrderReception>(
    '/admin/oms-recycle-order/reception/unbind',
    { id },
  );
};
